import React, { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import PatientNavbar from "../components/PatientNavbar";
import api from "../utils/api";
import { messaging } from "../firebaseConfig";
import { getToken, onMessage } from "firebase/messaging";
import "./PatientDashboard.css";

const PatientDashboard = () => {
  const { patientUser, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const { userId: urlPatientId } = useParams();
  const [userName, setUserName] = useState("Guest");
  const [userId, setUserId] = useState(urlPatientId || "");
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [healthTips, setHealthTips] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPatientProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('patientAccessToken') || patientUser.accessToken;
      const profileResponse = await api.get("/patients/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (profileResponse.data.accountStatus === 'deactivated') {
        setError("Your account has been deactivated. Please contact support at support@truemedicine.com.");
        return false;
      }
      if (profileResponse.data.accountStatus === 'banned') {
        setError("Your account has been banned. Please contact support at support@truemedicine.com.");
        return false;
      }
      setUserName(profileResponse.data.name || "Guest");
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      setError(`Failed to verify account status: ${errorMsg}`);
      if (errorMsg === "Unauthorized: Token expired") {
        setTimeout(() => navigate('/login?type=patient'), 1000);
      }
      return false;
    }
  }, [patientUser, navigate]);

  const fetchAppointments = useCallback(async (patientId) => {
    try {
      const token = localStorage.getItem('patientAccessToken') || patientUser.accessToken;
      const response = await api.get(`/appointments/patient/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allAppointments = response.data || [];
      const currentDate = new Date();
      setUpcomingAppointments(allAppointments.filter(
        (app) => new Date(app.rescheduledDate || app.date) >= currentDate && app.status.toLowerCase() !== "completed"
      ));
      setPastAppointments(allAppointments.filter(
        (app) => new Date(app.rescheduledDate || app.date) < currentDate || app.status.toLowerCase() === "completed"
      ));
      return allAppointments;
    } catch (error) {
      setUpcomingAppointments([]);
      setPastAppointments([]);
      console.error('Failed to fetch appointments:', error.response?.data?.error || error.message);
      return [];
    }
  }, [patientUser]);

  const fetchNotifications = useCallback(async (patientId, appointments) => {
    try {
      const token = localStorage.getItem('patientAccessToken') || patientUser.accessToken;
      const response = await api.get(`/notifications/patient/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const notificationsData = response.data || [];
      const appointmentMap = new Map(appointments.map(app => [app._id, app]));
      const enrichedNotifications = notificationsData.map(notification => {
        let appointmentDate = "N/A";
        let completedDate = "N/A";
        let doctorName = "Unknown";
        let updatedMessage = notification.message;

        if (notification.appointmentId && appointmentMap.has(notification.appointmentId)) {
          const appointment = appointmentMap.get(notification.appointmentId);
          appointmentDate = new Date(appointment.date).toLocaleString();
          completedDate = appointment.status.toLowerCase() === "completed" && appointment.completedAt
            ? new Date(appointment.completedAt).toLocaleString()
            : "N/A";
          doctorName = appointment.doctorId?.name || "Unknown";
          if (updatedMessage.toLowerCase().includes("completed by dr.")) {
            updatedMessage += ` Completed Appointment Date: ${completedDate}`;
          } else if (updatedMessage.toLowerCase().includes("rejected by dr.") || updatedMessage.toLowerCase().includes("accepted by dr.")) {
            updatedMessage += ` Appointment Date: ${appointmentDate}`;
          }
        }
        const isPrescription = notification.message.toLowerCase().includes("prescription");
        return { ...notification, message: updatedMessage, appointmentDate, completedDate, doctorName, isPrescription };
      });
      setNotifications(enrichedNotifications);
    } catch (error) {
      setNotifications([]);
    }
  }, [patientUser]);

  const fetchHealthTips = useCallback(async () => {
    try {
      const token = localStorage.getItem('patientAccessToken') || patientUser.accessToken;
      const response = await api.get("/health-tips", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHealthTips(response.data || []);
    } catch (error) {
      setHealthTips([]);
    }
  }, [patientUser]);

  const fetchRecommendations = useCallback(async () => {
    try {
      const token = localStorage.getItem('patientAccessToken') || patientUser.accessToken;
      const response = await api.get("/recommendations", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecommendations(response.data || []);
    } catch (error) {
      setRecommendations([]);
    }
  }, [patientUser]);

  const registerPushNotifications = async () => {
    if (!patientUser) return;
  
    if (!("serviceWorker" in navigator) || !("Notification" in window)) {
      setMessage("Push notifications are not supported in this browser.");
      console.error("Push notifications not supported");
      return;
    }
  
    try {
      let permission = Notification.permission;
      console.log("Notification permission:", permission);
      if (permission === "default") {
        permission = await Notification.requestPermission();
        console.log("Permission after request:", permission);
      }
      if (permission !== "granted") {
        setMessage("Please enable notifications for consultation alerts.");
        return;
      }
  
      const swUrl = "/service-worker.js";
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        console.log("Unregistered old service worker:", registration);
      }
      const registration = await navigator.serviceWorker.register(swUrl, { scope: "/" });
      console.log("Service worker registered:", registration);
  
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) throw new Error("VAPID key not set in environment");
  
      const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
      console.log("FCM token:", token);
      if (!token) throw new Error("No FCM token returned");
  
      const storedToken = localStorage.getItem("fcmToken");
      if (storedToken !== token) {
        await api.post(
          "/patients/register-push-token",
          { patientId: patientUser.id, token, overwrite: false },
          { headers: { Authorization: `Bearer ${patientUser.accessToken}` } }
        );
        localStorage.setItem("fcmToken", token);
        setMessage("Notifications enabled successfully!");
        console.log("Push token registered with backend");
      }
  
      onMessage(messaging, (payload) => {
        console.log("Foreground message received:", payload);
        const notification = new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: "/favicon.ico",
          data: { url: payload.data.click_action || "/" },
        });
        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          navigate(payload.data.appointmentId ? `/patient/video-call/${payload.data.appointmentId}` : "/");
        };
        setNotifications((prev) => [
          {
            _id: Date.now().toString(),
            message: payload.notification.body,
            createdAt: new Date().toISOString(),
            read: false,
          },
          ...prev,
        ]);
      });
    } catch (err) {
      setMessage(`Failed to enable notifications: ${err.message}`);
      console.error("Push notification setup failed:", err);
    }
  };

  useEffect(() => {
    if (authLoading) return;

    if (!patientUser || patientUser.role !== 'patient') {
      setError("Please log in as a patient to view this dashboard.");
      setTimeout(() => navigate('/login?type=patient'), 1000);
      return;
    }

    if (urlPatientId && urlPatientId !== patientUser.id) {
      setError("You are not authorized to view this patient dashboard.");
      setTimeout(() => navigate('/login?type=patient'), 1000);
      return;
    }

    setUserId(patientUser.id);

    const fetchCriticalData = async () => {
      setIsLoading(true);
      try {
        const isActive = await fetchPatientProfile();
        if (!isActive) return;

        const appointments = await fetchAppointments(patientUser.id);
        await fetchNotifications(patientUser.id, appointments);
        await registerPushNotifications();

        Promise.all([fetchHealthTips(), fetchRecommendations()]);
      } catch (error) {
        setError("Failed to load dashboard: " + (error.response?.data?.error || error.message));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCriticalData();
  }, [patientUser, authLoading, urlPatientId, navigate, fetchPatientProfile, fetchAppointments, fetchNotifications, fetchHealthTips, fetchRecommendations]);

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('patientAccessToken') || patientUser.accessToken;
      await api.put(`/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => (n._id === id ? { ...n, read: true } : n)));
    } catch (err) {
      setError('Failed to mark notification as read: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleViewProfile = () => navigate(`/patient-profile?userId=${userId}`);
  const handleJoinVideoCall = (appointmentId) => window.location.href = `/patient/video-call/${appointmentId}`;
  const handleViewRecords = () => navigate("/patient/view-records");
  const handleAppointmentDetails = () => navigate(`/patient/appointments`);
  const handleViewAppointmentDetails = (appointmentId) => navigate(`/patient/appointments/${appointmentId}`);

  if (error) {
    return (
      <div className="dashboard-container">
        <h2>{error}</h2>
        <button onClick={() => navigate('/login?type=patient')}>Go to Login</button>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <PatientNavbar userId={userId} />
      {isLoading ? (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading Your Turemedicine Dashboard Please Wait...</p>
        </div>
      ) : (
        <>
          <section className="welcome-section">
            <div className="welcome-header">
              <h1>Welcome back, {userName}</h1>
              <button className="edit-profile-btn" onClick={handleViewProfile}>Edit Profile</button>
            </div>
          </section>
          <div className="dashboard-grid">
            <section className="dashboard-card appointments">
              <h2>Your Upcoming Appointments</h2>
              <div className="upcoming-appointments">
                {upcomingAppointments.length > 0 ? (
                  upcomingAppointments.map((appointment) => (
                    <div key={appointment._id} className="appointment-card">
                      <p><strong>Doctor:</strong> {appointment.doctorId?.name || "Unknown Doctor"}</p>
                      <p>Appointment Date: {new Date(appointment.date).toLocaleString()}</p>
                      {appointment.rescheduledDate && (
                        <p>Updated Rescheduled Date: {new Date(appointment.rescheduledDate).toLocaleString()}</p>
                      )}
                      <p>Status: {appointment.status}</p>
                      <p><strong>Total Fee Paid:</strong> ₹{appointment.totalFee.toFixed(2)}</p>
                      {(appointment.status.toLowerCase() === "accepted" || appointment.status.toLowerCase() === "rescheduled") && (
                        <button onClick={() => handleJoinVideoCall(appointment._id)}>
                          Join Video Call
                        </button>
                      )}
                      <button
                        className="view-details-btn"
                        onClick={() => handleViewAppointmentDetails(appointment._id)}
                      >
                        View Details
                      </button>
                    </div>
                  ))
                ) : (
                  <p>No appointments booked. Please book an appointment.</p>
                )}
              </div>
              <button className="book-appointment-btn" onClick={() => navigate("/book-appointment")}>Book New Appointment</button>
            </section>
            <section className="dashboard-card past-appointments">
              <h2>Past Appointments</h2>
              <div className="past-appointments-list">
                {pastAppointments.length > 0 ? (
                  pastAppointments.map((appointment) => (
                    <div key={appointment._id} className="appointment-card">
                      <p><strong>Doctor:</strong> {appointment.doctorId?.name || "Unknown Doctor"}</p>
                      <p>Appointment Date: {new Date(appointment.date).toLocaleString()}</p>
                      {appointment.rescheduledDate && (
                        <p>Updated Rescheduled Date: {new Date(appointment.rescheduledDate).toLocaleString()}</p>
                      )}
                      {appointment.status.toLowerCase() === "completed" && appointment.completedAt && (
                        <p>Completed Date: {new Date(appointment.completedAt).toLocaleString()}</p>
                      )}
                      <p>Status: {appointment.status}</p>
                      <p className={`completion-status ${appointment.status.toLowerCase() === "completed" ? "completed" : ""}`}>
                        {appointment.status.toLowerCase() === "completed" ? "✓ Completed" : "⏳ Consultation Pending"}
                      </p>
                      <button
                        className="view-details-btn"
                        onClick={() => handleViewAppointmentDetails(appointment._id)}
                      >
                        View Details
                      </button>
                    </div>
                  ))
                ) : (
                  <p>No appointments booked. Please book an appointment.</p>
                )}
              </div>
            </section>
            <section className="dashboard-card quick-actions">
              <h2>Quick Actions</h2>
              <div className="actions-grid">
                <button className="action-btn" onClick={() => navigate("/search-doctor")}>Find Doctor</button>
                <button className="action-btn" onClick={() => navigate("/symptom-checker")}>Symptom Checker</button>
                <button className="action-btn" onClick={handleViewRecords}>View Records</button>
                <button className="action-btn" onClick={() => navigate("/support")}>Get Support</button>
                <button className="action-btn" onClick={handleAppointmentDetails}>Appointment Details</button>
              </div>
            </section>
            <section className="dashboard-card notifications">
              <h2>Notifications</h2>
              <div className="notifications-wrapper">
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div
                      key={notification._id}
                      className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                    >
                      <div className="notification-details">
                        <p><strong>{notification.message}</strong></p>
                        {notification.isPrescription && notification.completedDate !== "N/A" && (
                          <p><strong>Completed Appointment Date:</strong> {notification.completedDate}</p>
                        )}
                        <p><strong>Created At:</strong> {new Date(notification.createdAt).toLocaleString()}</p>
                      </div>
                      <div className="notification-actions">
                        {notification.isPrescription && (
                          <button className="view-records-btn" onClick={handleViewRecords}>
                            View Records
                          </button>
                        )}
                        {!notification.read && (
                          <button className="mark-as-read-btn" onClick={() => markAsRead(notification._id)}>
                            Mark as Read
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No notifications</p>
                )}
              </div>
            </section>
            <section className="dashboard-card health-tips">
              <h2>Health Tips & Articles</h2>
              <div className="tips-carousel">
                {healthTips.length > 0 ? (
                  healthTips.map((tip) => (
                    <div key={tip._id} className="tip-card">
                      <h4>{tip.title}</h4>
                      <p>{tip.excerpt}</p>
                      <a href={tip.link}>Read More</a>
                    </div>
                  ))
                ) : (
                  <p>No health tips available</p>
                )}
              </div>
            </section>
            <section className="dashboard-card recommendations">
              <h2>Recommended for You</h2>
              <div className="recommendations-grid">
                {recommendations.length > 0 ? (
                  recommendations.map((rec) => (
                    <div key={rec._id} className="recommendation-card">
                      <img src={rec.image} alt={rec.title} />
                      <h4>{rec.title}</h4>
                      <p>{rec.description}</p>
                    </div>
                  ))
                ) : (
                  <p>No recommendations available</p>
                )}
              </div>
            </section>
          </div>
          {message && <div className="message">{message}</div>}
        </>
      )}
    </div>
  );
};

export default PatientDashboard;